import { } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { format } from 'date-fns'

interface CostTrendChartProps {
  data: Array<{
    date: string
    calls: number
    minutes: number
    cost: number
  }>
  loading?: boolean
}

export default function CostTrendChart({ data, loading = false }: CostTrendChartProps) {
  const formattedData = data.map(item => ({
    ...item,
    displayDate: format(new Date(item.date), 'MMM dd'),
    costDisplay: `$${item.cost.toFixed(2)}`
  }))

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Voice Costs</CardTitle>
          <CardDescription>Daily voice cost trend (30 days)</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center">
            <div className="animate-pulse text-muted-foreground">Loading chart...</div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Voice Costs</CardTitle>
        <CardDescription>Daily voice cost and call count (30 days)</CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={formattedData}>
            <defs>
              <linearGradient id="colorCost" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="hsl(142 70% 45%)" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="hsl(142 70% 45%)" stopOpacity={0.1}/>
              </linearGradient>
              <linearGradient id="colorCalls" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="hsl(262 80% 55%)" stopOpacity={0.6}/>
                <stop offset="95%" stopColor="hsl(262 80% 55%)" stopOpacity={0.05}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis
              dataKey="displayDate"
              className="text-xs"
              tick={{ fill: 'hsl(var(--muted-foreground))' }}
            />
            <YAxis
              yAxisId="cost"
              className="text-xs"
              tick={{ fill: 'hsl(var(--muted-foreground))' }}
              tickFormatter={(val) => `$${val}`}
            />
            <YAxis
              yAxisId="calls"
              orientation="right"
              className="text-xs"
              tick={{ fill: 'hsl(var(--muted-foreground))' }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px'
              }}
              labelStyle={{ color: 'hsl(var(--foreground))' }}
              formatter={(value: number, name: string) => {
                if (name === 'cost') return [`$${value.toFixed(2)}`, 'Cost']
                if (name === 'calls') return [value, 'Calls']
                return [value, name]
              }}
            />
            <Area
              yAxisId="cost"
              type="monotone"
              dataKey="cost"
              stroke="hsl(142 70% 45%)"
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorCost)"
            />
            <Area
              yAxisId="calls"
              type="monotone"
              dataKey="calls"
              stroke="hsl(262 80% 55%)"
              strokeWidth={1.5}
              fillOpacity={1}
              fill="url(#colorCalls)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  )
}
